import T from 'i18n-react'
import { createSelector } from 'reselect'

import {
  createSelectorWithPath,
  createSelectorWithEntity,
} from 'helpers/ComponentSelectorUtil'

const valueSelectorFunc = state => state.values
const ocBinValueSelectorFunc = state => ((state.values[0]) && state.values[0].hasOwnProperty('ocBin')) ? state.values[0].ocBin : 0
const baseValueSelectorFunc = state => (state.values[0]) ? state.values[0].baseClock : undefined
const boostValueSelectorFunc = state => (state.values[0]) ? state.values[0].boostClock : undefined
const offsetValueSelectorFunc = state => (state.values[0]) ? state.values[0].vfOffset : undefined
const tgpValueSelectorFunc = state => (state.values[0]) ? state.values[0].tgp : undefined
const overclockValueSelectorFunc = state => (state.values[0]) ? state.values[0].productOverclockId : undefined
const sourcesSelectorFunc = state => state.sources
const getValueSelector = (path, id) => createSelectorWithPath(`${path}.${id}`, valueSelectorFunc)

const sourcesResultFunc = (entities, refs) => {
  let sources = refs.map(({id, type}) => entities[type][id])
  let sorted = sources.sort((a, b) => a.boostClock - b.boostClock)
  return sorted
} 

// gets all unique OC Bin values
const ocBinSourcesSelectorHoc = (sourcesSelector) => {
  const resultFunc = (sources) => Array.from(new Set(sources.map(({ ocBin }) => ocBin)))
  return createSelector(sourcesSelector, resultFunc)
}

// gets all relevant base clock values 
const baseSourcesSelectorHoc = (sourcesSelector, ocBinValueSelector) => {
  const resultFunc = (sources, ocBin) => {
    // filter for selected oc_bin 
    const filteredSources = sources.filter(source => source.ocBin === ocBin)
    // Get base clocks and turn array into set to remove duplicates
    return Array.from(filteredSources.map(({ baseClock }) => baseClock))
    // return filteredSources
  }
  return createSelector(sourcesSelector, ocBinValueSelector, resultFunc)
}

// gets all boost clock values for current oc_bin and base clock
const boostSourcesSelectorHoc = (sourcesSelector, baseValueSelector, ocBinValueSelector) => {
  const resultFunc = (sources, baseValue, ocBin) => {
    const filteredSources = sources.filter(source => source.baseClock === baseValue && source.ocBin === ocBin)
    return filteredSources.map(({ boostClock }) => boostClock)
  }
  return createSelector(sourcesSelector, baseValueSelector, ocBinValueSelector, resultFunc)
}

// gets min boost clock value
const minBoostSelectorHoc = (boostSourcesSelector) => {
  const resultFunc = (boostSources) => Math.min(...boostSources)
  return createSelector(boostSourcesSelector, resultFunc)
}

// gets max boost clock value
const maxBoostSelectorHoc = (boostSourcesSelector) => {
  const resultFunc = (boostSources) => Math.max(...boostSources)
  return createSelector(boostSourcesSelector, resultFunc)
}

export {
  getValueSelector,
}
// Inject dependencies for selectors
export default (path) => {
  const withPath = createSelectorWithPath(path)

  const ocBinValueSelector = withPath(ocBinValueSelectorFunc)
  const baseValueSelector = withPath(baseValueSelectorFunc)
  const boostValueSelector = withPath(boostValueSelectorFunc)
  const offsetValueSelector = withPath(offsetValueSelectorFunc)
  const tgpValueSelector = withPath(tgpValueSelectorFunc)

  const overclockValueSelector = withPath(overclockValueSelectorFunc)
  const sourcesSelectorWithPath = withPath(sourcesSelectorFunc)
  const sourcesSelector = createSelectorWithEntity(sourcesSelectorWithPath, sourcesResultFunc)
  
  const ocBinSourcesSelector = ocBinSourcesSelectorHoc(sourcesSelector)
  const baseSourcesSelector = baseSourcesSelectorHoc(sourcesSelector, ocBinValueSelector)
  const boostSourcesSelector = boostSourcesSelectorHoc(sourcesSelector, baseValueSelector, ocBinValueSelector)

  const minBoostSelector = minBoostSelectorHoc(boostSourcesSelector)
  const maxBoostSelector = maxBoostSelectorHoc(boostSourcesSelector)

  return {
    ocBinValueSelector,
    baseValueSelector,
    boostValueSelector,
    offsetValueSelector,
    tgpValueSelector,
    overclockValueSelector,
    sourcesSelector,
    ocBinSourcesSelector,
    baseSourcesSelector,
    boostSourcesSelector,
    minBoostSelector,
    maxBoostSelector,
  }
}
